
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useEmailTemplates } from '@/hooks/useEmailTemplates';
import { EmailTemplate } from '@/types/emailTemplates';
import { RefreshCw } from 'lucide-react';

interface CreateEmailTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: () => void;
}

// Define a schema for form validation
const formSchema = z.object({
  name: z.string().min(3, { message: 'Name must be at least 3 characters' }),
  template_id: z.string().min(2, { message: 'Template ID is required' })
    .regex(/^[a-zA-Z0-9-_]+$/, { 
      message: 'Template ID can only contain letters, numbers, hyphens, and underscores' 
    }),
  description: z.string().optional(),
  subject: z.string().min(1, { message: 'Subject is required' }),
  html_content: z.string().min(10, { message: 'HTML content is required' }),
  text_content: z.string().optional(),
  category: z.string().min(1, { message: 'Category is required' }),
  variables: z.string().optional()
});

export function CreateEmailTemplateDialog({
  open,
  onOpenChange,
  onCreated
}: CreateEmailTemplateDialogProps) {
  const { createTemplate, loadingAction } = useEmailTemplates();
  const [formError, setFormError] = useState<string | null>(null);
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      template_id: '',
      description: '',
      subject: '',
      html_content: '<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">\n  <h1>Hello {{name}}</h1>\n  <p>This is a sample template.</p>\n</div>',
      text_content: '',
      category: 'notifications',
      variables: '{"name": "User\'s name"}'
    }
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setFormError(null);
      
      // Parse variables from string to object
      let parsedVariables = {};
      if (values.variables) {
        try {
          parsedVariables = JSON.parse(values.variables);
        } catch (err) {
          setFormError("Invalid JSON format for variables");
          return;
        }
      }

      const templateData: Omit<EmailTemplate, 'id' | 'created_at' | 'updated_at' | 'version'> = {
        name: values.name,
        template_id: values.template_id,
        description: values.description || null,
        subject: values.subject,
        html_content: values.html_content,
        text_content: values.text_content || null,
        is_active: true,
        variables: parsedVariables as Record<string, string>,
        category: values.category,
        created_by: null
      };

      const result = await createTemplate(templateData);
      
      if (result) {
        onOpenChange(false);
        if (onCreated) onCreated();
        form.reset();
      }
    } catch (error: any) {
      setFormError(error.message || "An error occurred while creating the template");
    }
  };

  const categoryOptions = [
    { value: 'onboarding', label: 'Onboarding' },
    { value: 'account', label: 'Account' },
    { value: 'courses', label: 'Courses' },
    { value: 'certificates', label: 'Certificates' },
    { value: 'notifications', label: 'Notifications' },
    { value: 'transaction', label: 'Transaction' },
    { value: 'marketing', label: 'Marketing' }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[700px] w-full">
        <DialogHeader>
          <DialogTitle>Create Email Template</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Template Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Welcome Email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="template_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Template ID</FormLabel>
                    <FormControl>
                      <Input placeholder="welcome-email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input placeholder="Sent when a new user signs up" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="subject"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Subject</FormLabel>
                    <FormControl>
                      <Input placeholder="Welcome to DriveCourses!" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {categoryOptions.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="html_content"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>HTML Content</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="<div>Hello {{name}}</div>" 
                      className="font-mono h-40" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="text_content"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Plain Text Content (Optional)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Hello {{name}}" 
                      className="font-mono h-20" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="variables"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Variables (JSON Format)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder={"{ \"name\": \"User's name\", \"course\": \"Course name\" }"}
                      className="font-mono h-20" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            {formError && (
              <div className="bg-red-50 p-3 rounded-md border border-red-200 text-red-800 text-sm">
                {formError}
              </div>
            )}
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => onOpenChange(false)} 
                className="mr-2">
                Cancel
              </Button>
              <Button type="submit" disabled={loadingAction}>
                {loadingAction ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Template'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
